'use client';

import {Share2} from 'lucide-react';
import {useEffect, useRef, useState} from 'react';
import {usePathname} from 'next/navigation';
import {trackGuideShared} from 'iq-interview_guides/tracking';
import {glassSurface, type HeroTone} from 'iq-interview_guides/components/hero_glass';

function X_TwitterIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='16 0 16 50' width='28px' height='28px'>
      <path d='M 11 4 C 7.134 4 4 7.134 4 11 L 4 39 C 4 42.866 7.134 46 11 46 L 39 46 C 42.866 46 46 42.866 46 39 L 46 11 C 46 7.134 42.866 4 39 4 L 11 4 z M 13.085938 13 L 21.023438 13 L 26.660156 21.009766 L 33.5 13 L 36 13 L 27.789062 22.613281 L 37.914062 37 L 29.978516 37 L 23.4375 27.707031 L 15.5 37 L 13 37 L 22.308594 26.103516 L 13.085938 13 z M 16.914062 15 L 31.021484 35 L 34.085938 35 L 19.978516 15 L 16.914062 15 z' />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24'>
      <path
        fill='#0288D1'
        d='M 24,20.666667 C 24,22.508 22.508,24 20.666667,24 H 3.3333334 C 1.4926667,24 0,22.508 0,20.666667 V 3.3333334 C 0,1.492 1.4926667,0 3.3333334,0 H 20.666667 C 22.508,0 24,1.492 24,3.3333334 Z'
      />
      <path
        fill='#ffffff'
        d='M 4,8.6666665 H 7.3333335 V 20 H 4 Z M 5.6566665,7.3333335 H 5.638 C 4.6433333,7.3333335 4,6.592 4,5.666 4,4.72 4.6633333,4 5.676,4 6.69,4 7.3146665,4.72 7.3333335,5.666 7.3333335,6.5913335 6.69,7.3333335 5.6566665,7.3333335 Z M 20,20 h -3.333333 v -6.066 c 0,-1.465334 -0.816667,-2.465334 -2.128,-2.465334 -1.000667,0 -1.542,0.674667 -1.804667,1.326668 C 12.638,13.028667 12.666667,13.674 12.666667,14 v 6 H 9.3333335 V 8.6666665 H 12.666667 V 10.410667 C 13.147334,9.6666665 13.9,8.6666665 15.825333,8.6666665 c 2.385333,0 4.174,1.5000005 4.174,4.8493335 z'
      />
    </svg>
  );
}

function RedditIcon() {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24'>
      <path
        fill='#FF4500'
        d='M 24,20.666667 C 24,22.508 22.508,24 20.666667,24 H 3.3333334 C 1.4926667,24 0,22.508 0,20.666667 V 3.3333334 C 0,1.492 1.4926667,0 3.3333334,0 H 20.666667 C 22.508,0 24,1.492 24,3.3333334 Z'
      />
      <path
        fill='#ffffff'
        d='M17.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z'
      />
    </svg>
  );
}

type Provider = 'twitter' | 'facebook' | 'linkedin' | 'reddit';

export function ShareButton({text, tone = 'onLight'}: {text?: string; tone?: HeroTone}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const slug = pathname.split('/').filter(Boolean).at(-1) ?? '';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShare = (provider: Provider) => {
    const pageURL = new URL(window.location.href);
    pageURL.searchParams.set('utm_source', provider);
    let sharingURL = '';
    const encodedText = encodeURIComponent(`${text ?? ''} via @interview_query`);
    switch (provider) {
      case 'twitter':
        sharingURL = `https://twitter.com/intent/tweet?url=${pageURL}&text=${encodedText}`;
        break;

      case 'linkedin':
        sharingURL = `https://www.linkedin.com/sharing/share-offsite/?url=${pageURL}`;
        break;
      case 'reddit':
        sharingURL = `https://www.reddit.com/submit?url=${pageURL}&title=${encodedText}`;
        break;
    }
    void trackGuideShared(provider, slug);
    window.open(sharingURL);
    setOpen(false);
  };

  return (
    <div className='relative flex' ref={ref}>
      <button
        className={`h-full aspect-square flex items-center justify-center rounded-lg transition-colors ${glassSurface(tone)}`}
        aria-label='Share'
        onClick={() => setOpen((v) => !v)}>
        <Share2 className='w-4 h-4 text-gray-700' />
      </button>
      {open && (
        <div className='absolute right-0 top-full mt-2 flex gap-1 bg-white rounded-lg border border-gray-200 shadow-lg p-2 z-50'>
          <button
            onClick={() => handleShare('twitter')}
            className='p-2 rounded-md hover:bg-gray-100 transition-colors'
            title='Share on X (Twitter)'>
            <X_TwitterIcon />
          </button>

          <button
            onClick={() => handleShare('linkedin')}
            className='p-2 rounded-md hover:bg-gray-100 transition-colors'
            title='Share on LinkedIn'>
            <LinkedInIcon />
          </button>
          <button
            onClick={() => handleShare('reddit')}
            className='p-2 rounded-md hover:bg-gray-100 transition-colors'
            title='Share on Reddit'>
            <RedditIcon />
          </button>
        </div>
      )}
    </div>
  );
}
